import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId } from '@/annotations'
import { Avatar } from '@/components/Avatar'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { MCancelrestore } from '@/screens/m-cancelrestore/MCancelrestore'
import { JOB_STATUS_CHIP } from '@/screens/shared/jobFormatting'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestoreJob = components['schemas']['RestoreJob']

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  PARTIAL_SUCCESS: 'Partial success',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
  CANCELLED: 'Cancelled',
}

const KIND_LABEL: Record<string, string> = {
  REPOSITORY: 'Repository',
  REF: 'Ref',
  SETTINGS_POLICY: 'Settings & policy',
  ACCESS_INTEGRATION: 'Access & integrations',
  CI_DEPLOYMENT: 'CI & deployment',
  REVIEW_STATE: 'Pull request state',
  PROJECT_POLICY: 'Project policy',
  WORKSPACE_WEBHOOK: 'Workspace webhooks',
}

/**
 * `v-jobsrestore` — process/09-SCREEN-SPECS/v-jobsrestore.md. `Export CSV` has no operation
 * (`D89`). A restore job is named by handle, timestamp, type AND destination (rule 2, §9.h A4) —
 * one more field than a backup job, since a cross-workspace restore's destination can differ from
 * the source workspace this list is scoped to.
 *
 * **One of the spec's own three `grid-tools` `icon-button`s added W157** — a real "Refresh"
 * invalidating the same query the pager already reads. No `data-act` — the Actions table names
 * only `Cancel`. The other two ("Clear filters", a column chooser) are not built: this screen has
 * no filter state to clear and no column-visibility state to back a chooser.
 *
 * **`avatar` added W161** — beside `triggered_by.display_name`, shown only when present.
 *
 * **`panel`/`grid-filter-row` added W168** — a real status filter using `restoreListJobs`'s own
 * real `status` query parameter, matching `v-jobsbackup`'s identical fix (W157); the table wrapped
 * in a real `panel`. **`segment-option` (its own spec names it "in `pager`") is confirmed the same
 * conflict `v-backupjob`/`v-restorejob` already found** — a numbered pager directly contradicts
 * §32.8/rule 25's "no page number, cursor-only" rule enforced everywhere else in this build.
 *
 * **A "Filter by type" filter added (visual-diff sweep finding)** — `restoreListJobs` carries a
 * real `kind` query parameter (`components['parameters']` — confirmed in the generated schema
 * before adding this, the same check `v-health`'s own category filter and `v-jobsbackup`'s status
 * filter were built against), so this is a second server-side filter alongside `status`, not a
 * client-side one over an unpaged list.
 *
 * **The status chip now uses `JOB_STATUS_CHIP` (W172)** — see `jobFormatting.ts`'s own doc comment
 * for the prototype evidence; was `variant={status === 'FAILED' ? 'default' : 'plain'}`, flat blue
 * or flat grey regardless of the real outcome.
 */
export function VJobsrestore() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [cancelling, setCancelling] = useState<RestoreJob | undefined>()
  const [status, setStatus] = useState<components['schemas']['JobStatus'] | ''>('')
  const [kind, setKind] = useState<components['schemas']['RestoreKind'] | ''>('')

  const list = useQuery({
    queryKey: ['restore-jobs', orgId, workspaceId, pager.cursor, status, kind],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/restores', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: pager.cursor, status: status || undefined, kind: kind || undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const problem = list.error instanceof ApiProblemError ? list.error.problem : undefined
  const rows = (list.data?.items ?? []) as unknown as readonly RestoreJob[]

  return (
    <div data-scr={unitId('v-jobsrestore')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Jobs: restore</h1>
        <div data-cmp="grid-tools">
          <IconButton
            data-act={undefined}
            icon="i-refresh"
            label="Refresh"
            disabled={list.isFetching}
            onClick={() => queryClient.invalidateQueries({ queryKey: ['restore-jobs', orgId, workspaceId] })}
          />
          <Button data-act={undefined} ghost disabled title="No export exists for this list (D89)">
            Export CSV
          </Button>
        </div>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-2">
        <div data-cmp="grid-filter-row" className="p-4 border-b border-bd flex gap-3">
          <select
            value={status}
            onChange={(e) => {
              pager.reset()
              setStatus(e.target.value as components['schemas']['JobStatus'] | '')
            }}
            className="border border-bd2 rounded-1 py-2 px-3 text-small"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={kind}
            onChange={(e) => {
              pager.reset()
              setKind(e.target.value as components['schemas']['RestoreKind'] | '')
            }}
            className="border border-bd2 rounded-1 py-2 px-3 text-small"
          >
            <option value="">All types</option>
            {Object.entries(KIND_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <GridTable
          onRowClick={(restoreJob) => navigate(`/jobs/restore/${restoreJob.job.job_id}`)}
          columns={[
            { key: 'job', header: 'Job', render: (restoreJob) => <span className="font-mono">{restoreJob.job.handle}</span> },
            { key: 'kind', header: 'Kind', render: (restoreJob) => KIND_LABEL[restoreJob.restore_kind] ?? restoreJob.restore_kind },
            { key: 'destination', header: 'Destination', render: (restoreJob) => restoreJob.destination.workspace_name },
            {
              key: 'status',
              header: 'Status',
              render: (restoreJob) => (
                <>
                  <Chip variant={JOB_STATUS_CHIP[restoreJob.job.status] ?? 'plain'}>{STATUS_LABEL[restoreJob.job.status] ?? restoreJob.job.status}</Chip>
                  {restoreJob.secured_values_outstanding && restoreJob.secured_values_outstanding.numerator > 0 && (
                    <Note>{restoreJob.secured_values_outstanding.caption} still to enter</Note>
                  )}
                </>
              ),
            },
            { key: 'started', header: 'Started', render: (restoreJob) => (restoreJob.started_at ? restoreJob.started_at.display : '—') },
            { key: 'duration', header: 'Duration', render: (restoreJob) => (restoreJob.duration ? restoreJob.duration.display : '—') },
            {
              key: 'triggeredBy',
              header: 'Triggered by',
              render: (restoreJob) =>
                restoreJob.triggered_by?.display_name ? (
                  <>
                    <Avatar name={restoreJob.triggered_by.display_name} />
                    {restoreJob.triggered_by.display_name}
                  </>
                ) : (
                  '—'
                ),
            },
            {
              key: 'actions',
              header: '',
              stopRowClick: true,
              render: (restoreJob) =>
                restoreJob.job.status === 'RUNNING' || restoreJob.job.status === 'PENDING' ? (
                  restoreJob.cancellation ? (
                    <Note>Cancelling…</Note>
                  ) : (
                    <Button data-act={undefined} ghost onClick={() => setCancelling(restoreJob)}>
                      Cancel
                    </Button>
                  )
                ) : null,
            },
          ]}
          rows={rows}
          getRowKey={(restoreJob) => restoreJob.job.job_id}
          emptyMessage={list.isLoading ? '' : 'No jobs match these filters.'}
        />

        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>Paged by cursor only — no total is served for this list.</Note>
          <Pager
            hasPrev={pager.hasPrev}
            hasNext={Boolean(list.data?.next_cursor)}
            loading={list.isFetching}
            onPrev={pager.goPrev}
            onNext={() => {
              if (list.data?.next_cursor) pager.goNext(list.data.next_cursor)
            }}
          />
        </div>
      </div>

      {cancelling && (
        <MCancelrestore
          orgId={orgId}
          workspaceId={workspaceId}
          job={cancelling.job}
          restoreKindLabel={KIND_LABEL[cancelling.restore_kind] ?? cancelling.restore_kind}
          onDismiss={() => setCancelling(undefined)}
          onCancelled={() => {
            setCancelling(undefined)
            queryClient.invalidateQueries({ queryKey: ['restore-jobs', orgId, workspaceId] })
          }}
        />
      )}
    </div>
  )
}
